import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getLanguages } from 'app/entities/language/language.reducer';
import { getEntities as getTours } from 'app/entities/tour/tour.reducer';
import { getEntity, updateEntity, createEntity, reset } from './tour-content.reducer';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

export const TourContentUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const languages = useAppSelector(state => state.language.entities);
  const tours = useAppSelector(state => state.tour.entities);
  const tourContentEntity = useAppSelector(state => state.tourContent.entity);
  const loading = useAppSelector(state => state.tourContent.loading);
  const updating = useAppSelector(state => state.tourContent.updating);
  const updateSuccess = useAppSelector(state => state.tourContent.updateSuccess);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/tour-content' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setEditorState(EditorState.createEmpty());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getLanguages({}));
    dispatch(getTours({}));
  }, []);

  useEffect(() => {
    if (tourContentEntity) {
      setFormData({
        ...tourContentEntity,
        language: tourContentEntity?.language?.id,
        tour: tourContentEntity?.tour?.id,
      });
    }
  }, [tourContentEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (!loading && !isNew) {
      if (tourContentEntity !== undefined && tourContentEntity.atAGlance !== undefined) {
        const contentBlock = htmlToDraft(tourContentEntity.atAGlance);
        if (contentBlock) {
          const { contentBlocks, entityMap } = contentBlock;
          const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
          setEditorState(EditorState.createWithContent(contentState));
        }
      }
    }
  }, [loading, isNew]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    if (!formData.tour) {
      newErrors.tour = 'Tour is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const ataglance = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    const values = { ...formData };
    values.language = values.language || null;

    const entity = {
      ...tourContentEntity,
      ...formData,
      atAGlance: ataglance,
      language: languages.find(it => it.id.toString() === formData.language?.toString()),
      tour: tours.find(it => it.id.toString() === formData.tour?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const onEditorStateChange = editorSt => {
    setEditorState(editorSt);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.tourContent.home.createOrEditLabel">Create or edit a TourContent</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.code')}
              id="tour-content-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!errors.tour}>
                  <InputLabel id="tour-content-tour-label">{translate('jhipsterSampleApplicationApp.tourContent.tour')}</InputLabel>
                  <Select
                    labelId="tour-content-tour-label"
                    id="tour-content-tour"
                    name="tour"
                    data-cy="tour"
                    value={formData.tour || ''}
                    label={translate('jhipsterSampleApplicationApp.tourContent.tour')}
                    onChange={handleSelectChange('tour')}
                  >
                    <MenuItem value="" key="0">
                      <em>Select...</em>
                    </MenuItem>
                    {tours
                      ? tours.map(otherEntity => (
                          <MenuItem value={otherEntity.id} key={otherEntity.id}>
                            {otherEntity.code}
                          </MenuItem>
                        ))
                      : null}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel id="tour-content-language-label">{translate('jhipsterSampleApplicationApp.tourContent.language')}</InputLabel>
                  <Select
                    labelId="tour-content-language-label"
                    id="tour-content-language"
                    name="language"
                    data-cy="language"
                    value={formData.language || ''}
                    label={translate('jhipsterSampleApplicationApp.tourContent.language')}
                    onChange={handleSelectChange('language')}
                  >
                    <MenuItem value="" key="0">
                      <em>Select...</em>
                    </MenuItem>
                    {languages
                      ? languages.map(otherEntity => (
                          <MenuItem value={otherEntity.id} key={otherEntity.id}>
                            {otherEntity.code}
                          </MenuItem>
                        ))
                      : null}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.title')}
              id="tour-content-title"
              name="title"
              data-cy="title"
              value={formData.title || ''}
              onChange={handleChange('title')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.shortDescription')}
              id="tour-content-shortDescription"
              name="shortDescription"
              data-cy="shortDescription"
              value={formData.shortDescription || ''}
              onChange={handleChange('shortDescription')}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.data')}
              id="tour-content-data"
              name="data"
              data-cy="data"
              value={formData.data || ''}
              onChange={handleChange('data')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.meta')}
              id="tour-content-meta"
              name="meta"
              data-cy="meta"
              value={formData.meta || ''}
              onChange={handleChange('meta')}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.cancellation')}
              id="tour-content-cancellation"
              name="cancellation"
              data-cy="cancellation"
              value={formData.cancellation || ''}
              onChange={handleChange('cancellation')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.activityPath')}
              id="tour-content-activityPath"
              name="activityPath"
              data-cy="activityPath"
              value={formData.activityPath || ''}
              onChange={handleChange('activityPath')}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {translate('jhipsterSampleApplicationApp.tourContent.atAGlance')}
              </Typography>
              <Box sx={{ border: '1px solid #ccc', minHeight: 200 }}>
                <Editor
                  id="tour-content-atAGlance"
                  wrapperClassName="wrapper"
                  editorClassName="editor"
                  toolbarClassName="toolbar"
                  editorState={editorState}
                  onEditorStateChange={onEditorStateChange}
                  toolbar={{
                    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'link', 'history'],
                  }}
                />
              </Box>
            </Box>
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.inDetail')}
              id="tour-content-inDetail"
              name="inDetail"
              data-cy="inDetail"
              value={formData.inDetail || ''}
              onChange={handleChange('inDetail')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.whatIsIncluded')}
              id="tour-content-whatIsIncluded"
              name="whatIsIncluded"
              data-cy="whatIsIncluded"
              value={formData.whatIsIncluded || ''}
              onChange={handleChange('whatIsIncluded')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.youCanAdd')}
              id="tour-content-youCanAdd"
              name="youCanAdd"
              data-cy="youCanAdd"
              value={formData.youCanAdd || ''}
              onChange={handleChange('youCanAdd')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.importantInformation')}
              id="tour-content-importantInformation"
              name="importantInformation"
              data-cy="importantInformation"
              value={formData.importantInformation || ''}
              onChange={handleChange('importantInformation')}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.tourContent.extraInfo')}
              id="tour-content-extraInfo"
              name="extraInfo"
              data-cy="extraInfo"
              value={formData.extraInfo || ''}
              onChange={handleChange('extraInfo')}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/tour-content"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TourContentUpdate;
